import { pricingTableByKey } from '@cutr/constants/windows/sorpetaler-pricing-tables';
import {
  ConfigurationValues,
  isSupplierQuoteAvailable,
  Position,
  PricingLineItem,
  resolveAreaConfigurations,
  resolveFrameBeamWidthsMM,
  resolveSystem,
  WindowOrder,
  WindowsCurrency,
} from '@cutr/constants/windows/windows';
import { getFrameDimensions } from '@cutr/constants/windows/windows-geometry.ts';
import { useFlag } from '@unleash/proxy-client-react';
import cn from 'classnames';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Card from '@/blocks/Card';
import { InfoMessage } from '@/blocks/InfoMessage';
import { Icon, Table } from '@/primitives/Icons';
import { currencyFormatter } from '@/utils/format';

import { useWindowLoaderData } from '../api/windowStore';
import { Disclosure } from '../primitives/Disclosure';
import {
  AreaLevelConfigurations,
  OrderLevelConfigurations,
  PositionLevelConfigurations,
} from './configurations/Configurations';
import { ConfigurationsProvider } from './configurations/ConfigurationsContext';
import styles from './OrderSummary.module.css';
import { useRenderStyles } from './renderer/renderStyles';
import { WindowReadOnly } from './renderer/WindowReadOnly';

type OrderSummaryProps = {
  order: WindowOrder;
};

export const OrderSummary = ({ order }: OrderSummaryProps) => {
  const { t } = useTranslation();
  const positions = order.positions;

  if (isSupplierQuoteAvailable(order.quoteReadyAt)) {
    return (
      <InfoMessage
        className={styles.pdfQuoteNotice}
        variant="default"
        message={t('reseller.orderSummary.pdfQuoteNotice')}
      />
    );
  }

  return (
    <div className={styles.orderSummary}>
      <OrderConfigurationSummary />
      <PositionsSummary positions={positions} />
    </div>
  );
};

const OrderConfigurationSummary = () => {
  const { configurations } = useWindowLoaderData();

  return (
    <ConfigurationsProvider configurations={configurations}>
      <OrderConfigurationReview />
    </ConfigurationsProvider>
  );
};

export const OrderConfigurationReview = () => {
  const isDebugPricingEnabled = useFlag('ctr-12165-debug-window-pricing');

  return (
    <Card className={styles.orderConfigurationSummary}>
      <OrderLevelConfigurations renderMode="readonly" />
      {isDebugPricingEnabled && <DetailedPricing />}
    </Card>
  );
};

const PositionsSummary = ({ positions }: { positions: Position[] }) => {
  const { t } = useTranslation();

  if (positions.length === 0) {
    return null;
  }

  return (
    <div>
      <h3>{t('reseller.orderSummary.positions')}</h3>
      <ul className={styles.positionsSummary}>
        {positions
          .filter((position) => position.windowSpec)
          .map((position) => (
            <PositionSummary key={position.id} position={position} />
          ))}
      </ul>
    </div>
  );
};

const PositionSummary = ({ position }: { position: Position }) => {
  const { title, quantity, windowSpec } = position;
  const { t } = useTranslation();
  const renderStyles = useRenderStyles('readOnly');
  const order = useWindowLoaderData();

  const isDebugPricingEnabled = useFlag('ctr-12165-debug-window-pricing');

  if (!windowSpec || Object.keys(windowSpec).length === 0) {
    return null;
  }

  const { frameHeightMM, frameWidthMM } = getFrameDimensions(
    windowSpec.frameShape
  );

  const systemClass = resolveSystem({
    orderConfigurations: order.configurations,
    positionConfigurations: position.configurations,
  });

  return (
    <li id={`position-summary-${position.id}`}>
      <div className="flexAlign">
        <h4>
          {t('reseller.orderSummary.position.title', { title, quantity })}
        </h4>
        {frameHeightMM && frameWidthMM && (
          <span>
            {t('reseller.orderSummary.frameDimensions', {
              height: frameHeightMM,
              width: frameWidthMM,
            })}
          </span>
        )}
      </div>
      <p />
      <div className={styles.positionSummary}>
        <WindowReadOnly
          windowSpec={{
            ...windowSpec,
            areaConfigurations: resolveAreaConfigurations({
              orderConfigurations: order.configurations,
              positionConfigurations: position.configurations,
              areaConfigurations: windowSpec.areaConfigurations,
            }),
          }}
          frameBeamWidthsMM={resolveFrameBeamWidthsMM({
            orderConfigurations: order.configurations,
            positionConfigurations: position.configurations,
          })}
          systemClass={systemClass?.value}
          renderStyles={renderStyles}
          positionId={position.id}
        />
        <div>
          <PositionConfigurations position={position} />
          {isDebugPricingEnabled && (
            <DetailedPricing positionId={position.id} />
          )}
        </div>
      </div>
    </li>
  );
};

const DetailedPricing = ({ positionId }: { positionId?: string }) => {
  const order = useWindowLoaderData();
  const lineItems = order.pricingLineItems.filter(
    (lineItem) => lineItem.positionId === positionId
  );

  if (lineItems.length === 0) {
    return null;
  }

  return (
    <Disclosure
      title="Detailed Pricing"
      level={5}
      style={{ marginTop: '1rem', paddingBottom: '1rem' }}
    >
      <PricingLineItems
        pricingLineItems={lineItems}
        currency={order.currency}
      />
    </Disclosure>
  );
};

const PositionConfigurations = ({ position }: { position: Position }) => {
  const { configurations, windowSpec } = position;
  const { t } = useTranslation();

  const areaConfigurations = windowSpec?.areaConfigurations ?? {};
  const hasAreaOverrides = Object.values(areaConfigurations).some(
    (configuration) => configuration?.sashbars
  );

  if (Object.keys(configurations).length === 0 && !hasAreaOverrides) {
    return (
      <div className={styles.noConfiguration}>
        {t('reseller.orderSummary.position.noConfiguration')}
      </div>
    );
  }

  return (
    <>
      <ConfigurationsProvider
        configurations={configurations as ConfigurationValues}
      >
        <PositionLevelConfigurations
          renderMode="readonly"
          title={t('reseller.common.configurationOverrides')}
        />
      </ConfigurationsProvider>
      <p />
      <AreaLevelConfigurations areaConfigurations={areaConfigurations} />
    </>
  );
};

/* eslint-disable i18next/no-literal-string */
const PricingTableExpanded = ({
  meta,
  currency,
}: {
  meta: NonNullable<PricingLineItem['meta']>;
  currency: WindowsCurrency;
}) => {
  const table = pricingTableByKey[meta.tableKey];
  if (!table) return null;

  // Arc-top tables are priced by width alone (1-D), so the server emits row: null.
  // A null row is how we tell an arc table apart from a normal width×height one.
  const isArc = meta.row === null;
  const cornerLabel = isArc ? 'width → (mm)' : 'height ↓ / width → (mm)';

  return (
    <div className={styles.pricingTableExpanded}>
      <div className={styles.pricingTableTitle}>{meta.tableLabel}</div>
      <table className={styles.pricingTableGrid}>
        <tbody>
          {table.map((tableRow, rowIdx) => (
            <tr key={rowIdx}>
              {tableRow.map((cell, colIdx) => {
                const isSelectedCol = colIdx === meta.col + 1;
                const isSelectedRow = !isArc && rowIdx === meta.row! + 1;
                const isHeader = rowIdx === 0 || colIdx === 0;
                const isCorner = rowIdx === 0 && colIdx === 0;
                const isSelectedCell =
                  isSelectedCol && (isArc ? rowIdx === 1 : isSelectedRow);

                let content;
                if (isCorner) {
                  content = cornerLabel;
                } else if (isArc && colIdx === 0 && rowIdx === 1) {
                  content = 'price';
                } else if (cell === null) {
                  content = '–';
                } else if (isHeader) {
                  content = cell;
                } else {
                  content = currencyFormatter(cell / 100, currency);
                }

                return (
                  <td
                    key={colIdx}
                    className={cn(styles.pricingTableCell, {
                      [styles.pricingTableHeader]: isHeader,
                      [styles.pricingTableCorner]: isCorner,
                      [styles.pricingTableHighlightCol]:
                        !isHeader && isSelectedCol,
                      [styles.pricingTableHighlightRow]:
                        !isHeader && isSelectedRow,
                      [styles.pricingTableHighlightCell]: isSelectedCell,
                    })}
                  >
                    {content}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const PricingTableTooltip = ({
  meta,
  currency,
}: {
  meta: NonNullable<PricingLineItem['meta']>;
  currency: WindowsCurrency;
}) => {
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number }>();

  // Center the fixed panel under the icon, then clamp so neither edge
  // overflows the viewport. Measured on hover (tooltip is visible via :hover
  // by the time mouseenter fires, so offsetWidth is valid).
  const position = () => {
    const icon = wrapperRef.current?.getBoundingClientRect();
    const width = tooltipRef.current?.offsetWidth ?? 0;
    if (!icon) return;
    const margin = 4;
    const centered = icon.left + icon.width / 2 - width / 2;
    const left = Math.max(
      margin,
      Math.min(centered, window.innerWidth - width - margin)
    );
    setCoords({ top: icon.bottom + 6, left });
  };

  return (
    <span
      ref={wrapperRef}
      className={styles.tableIconWrapper}
      onMouseEnter={position}
    >
      <Icon icon={<Table />} size={2} />
      <div
        ref={tooltipRef}
        className={styles.pricingTableTooltip}
        style={coords}
      >
        <PricingTableExpanded meta={meta} currency={currency} />
      </div>
    </span>
  );
};

export const PricingLineItems = ({
  pricingLineItems,
  currency,
}: {
  pricingLineItems: PricingLineItem[];
  currency: WindowsCurrency;
}) => {
  return (
    <ul className={styles.detailedPricing}>
      <li>
        <strong>category</strong>
        <strong>description</strong>
        <strong className={cn(styles.alignRight)}>quantity</strong>
        <strong className={cn(styles.alignCenter)}>&nbsp;</strong>
        <strong className={cn(styles.alignRight)}>unit amount</strong>
        <strong className={cn(styles.alignRight)}>&nbsp;</strong>
        <strong className={cn(styles.alignRight)}>totalAmount</strong>
      </li>
      {pricingLineItems.map(
        (
          { description, unitAmount, totalAmount, quantity, category, meta },
          index
        ) => (
          <li key={index}>
            <span className={cn(styles.cell, styles.descriptionCell)}>
              {category}
            </span>
            <span className={cn(styles.cell, styles.descriptionCell)}>
              {description}
              {meta && <PricingTableTooltip meta={meta} currency={currency} />}
            </span>

            <span className={cn(styles.cell, styles.alignRight)}>
              {quantity}
            </span>
            <span className={cn(styles.cell, styles.alignCenter)}>{'×'}</span>
            <span className={cn(styles.cell, styles.alignRight)}>
              {unitAmount !== null
                ? currencyFormatter(unitAmount / 100, currency)
                : '-'}
            </span>
            <span className={cn(styles.cell, styles.alignCenter)}>{'='}</span>
            <span className={cn(styles.cell, styles.alignRight)}>
              {totalAmount !== null ? (
                currencyFormatter(totalAmount / 100, currency)
              ) : (
                <i>manual quoting</i>
              )}
            </span>
          </li>
        )
      )}
    </ul>
  );
};
/* eslint-enable i18next/no-literal-string */
